import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';

import { Pros } from 'src/providers/pros/pros';
import { Booking } from 'src/providers/booking/booking';
import { Schedule } from 'src/providers/schedule/schedule';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { Integrations } from 'src/providers/integrations/integrations';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Util } from 'src/shared/util/util';
import { Loading } from 'src/shared/components/loading/loading';

import { WhyAddPrivateProsPage } from 'src/pages/why-add-private-pros/why-add-private-pros';

import validationUtils from 'src/shared/util/validation-utils';
import { parsePhoneNumber } from 'src/tidy-ui-components/components/form/input-international-phone/international-number.utils';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
  selector: 'tidy-job-form-popup',
  templateUrl: './job-form-popup.component.html',
  styleUrls: ['./job-form-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class JobFormPopupComponent {

  @Input() bookingId: number;
  @Input() jobId: number;
  @Input() booking: any;
  @Input() addressId: number;
  @Input() checklistItems: any[] = [];
  @Input() customFields: any[] = [];

  @Output() noteSaved = new EventEmitter<any>();
  @Output() checklistSaved = new EventEmitter<any>();
  @Output() customFieldSaved = new EventEmitter<{customFields: any[]}>();
  @Output() proChanged = new EventEmitter<{booking: any, jobId: number}>();

  isNotePopupOpen = false;
  existingNoteText = '';

  isChecklistPopupOpen = false;
  currentChecklistId: number | null = null;

  isCustomFieldPopupOpen = false;
  editingCustomField: any = null;

  showChangeProPopup = false;
  changeProForm: UntypedFormGroup;
  changeProSubmitText = '';
  changeProHeaderText = '';
  changeProErrorMessage = '';
  changeProExistingOrNewItems: any[] = [];
  changeProPrivateProItems: any[] = [];
  changeProCountryCode = '';
  changeProSubmitted = false;

  boundConfirmChangePro = this.confirmChangePro.bind(this);

  constructor(
    private fb: UntypedFormBuilder,
    private schedule: Schedule,
    private bookingProvider: Booking,
    private toDosProvider: ToDos,
    private integrations: Integrations,
    private prosService: Pros,
    private onboardingProvider: OnboardingProvider,
    private util: Util,
    private rightSidePanelService: RightSidePanelService,
    private cdr: ChangeDetectorRef
  ) {}

  // ── Note Popup ──

  openNotePopup(): void {
    const textNote = this.booking?.booking_notes?.find(note => note.type === 'text');
    this.existingNoteText = textNote?.text || '';
    this.isNotePopupOpen = true;
    this.cdr.markForCheck();
  }

  onNoteCancel(): void {
    this.isNotePopupOpen = false;
  }

  onNoteSave = async (value: string): Promise<void> => {
    try {
      const textNote = this.booking?.booking_notes?.find(note => note.type === 'text');
      const textNoteId = textNote?.id;
      const isAddingNew = !textNoteId && value && value.trim() !== '';
      const isUpdating = textNoteId && value && value.trim() !== '';
      const isDeleting = textNoteId && (!value || value.trim() === '');
      if (isAddingNew) {
        await this.schedule.createJobNote({
          booking_id: this.bookingId,
          type: 'text',
          text: value
        });
      } else if (isUpdating) {
        await this.schedule.updateJobNote(textNoteId, {
          id: textNoteId,
          type: 'text',
          text: value
        });
      } else if (isDeleting) {
        await this.schedule.deleteJobNote(textNoteId);
      }
      const updatedBooking = await this.schedule.getBooking(this.bookingId);
      this.isNotePopupOpen = false;
      this.noteSaved.emit(updatedBooking);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  // ── Checklist Popup ──

  async openChecklistPopup(): Promise<void> {
    try {
      this.currentChecklistId = this.booking?.booking?.task_list?.id || 0;
      this.isChecklistPopupOpen = true;
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  onChecklistCancel(): void {
    this.isChecklistPopupOpen = false;
  }

  onChecklistSave = async (value: any): Promise<void> => {
    try {
      const jobId = this.jobId;
      const listId = value === 0 ? null : value;
      await this.toDosProvider.assignListToJob([jobId], listId);
      const updatedBooking = await this.schedule.getBooking(this.bookingId);
      this.isChecklistPopupOpen = false;
      this.checklistSaved.emit(updatedBooking);
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  // ── Custom Field Popup ──

  editCustomFieldValue(field: { name: string; value: any; fieldKey?: string }): void {
    this.editingCustomField = field;
    this.isCustomFieldPopupOpen = true;
    this.cdr.markForCheck();
  }

  onCustomFieldCancel(): void {
    this.isCustomFieldPopupOpen = false;
    this.editingCustomField = null;
  }

  onCustomFieldSave = async (value: string): Promise<void> => {
    try {
      const definitions = await this.integrations.getCustomFields('Booking');
      const definition = definitions.find((def: any) => def.field_name === this.editingCustomField.name);
      if (!definition) {
        this.util.showError('Custom field definition not found');
        return;
      }
      const currentCustomFields = this.booking?.custom_fields || {};
      const updatedCustomFields = {
        ...currentCustomFields,
        [definition.field_key]: value
      };
      await this.integrations.updateCustomFieldValues(
        { custom_fields: updatedCustomFields },
        'Job',
        { id: this.bookingId }
      );
      const fieldIndex = this.customFields.findIndex(f => f.name === this.editingCustomField.name);
      if (fieldIndex !== -1) {
        this.customFields[fieldIndex].value = value || 'N/A';
      }
      this.booking.custom_fields = updatedCustomFields;
      this.isCustomFieldPopupOpen = false;
      this.editingCustomField = null;
      this.util.showSuccess('Custom field updated');
      this.customFieldSaved.emit({customFields: this.customFields});
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message);
    }
  }

  // ── Change Pro Popup ──

  @Loading('', true)
  async goToChangePro() {
    try {
      const privateProsList = await this.prosService.getAllPrivatePros();
      if (!privateProsList?.length) {
        const params = {
          booking: this.booking,
          addressId: this.addressId,
          privateProsList,
          categoryImage: this.booking?.service_type_details?.service_category?.icon_url
        };
        this.rightSidePanelService.navigateTo('why-add-private-pros', params, WhyAddPrivateProsPage);
        return;
      }
      this.initChangeProForm(privateProsList);
      this.showChangeProPopup = true;
      this.cdr.markForCheck();
    } catch(err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  private initChangeProForm(privateProsList: any[]) {
    this.changeProPrivateProItems = privateProsList.map(pro => ({
      viewValue: pro.name,
      value: pro.id
    }));
    const hasProsAssigned = this.booking?.assignment_status === 'assigned';
    this.changeProHeaderText = hasProsAssigned ? 'Change' : 'Assign';
    this.changeProExistingOrNewItems = [
      {
        viewValue: `${this.changeProHeaderText === 'Change' ? 'Change to' : 'Assign'} an existing pro`,
        value: 'existingPro'
      },
      {
        viewValue: `Add & ${this.changeProHeaderText === 'Change' ? 'change to' : 'assign'} a new pro`,
        value: 'newPro'
      }
    ];
    let currentProId = '';
    const currentHomekeepers = this.booking?.booking?.current_job?.homekeepers;
    if (hasProsAssigned && currentHomekeepers?.[0]?.id) {
      const currentHkId = currentHomekeepers[0].id;
      const matchingPro = this.changeProPrivateProItems.find(item => item.value === currentHkId);
      if (matchingPro) {
        currentProId = matchingPro.value;
      }
    }
    this.changeProForm = this.fb.group({
      existingOrNew: ['existingPro'],
      privatePro: [currentProId, Validators.required],
      name: [''],
      email: [''],
      phone: ['']
    });
    this.changeProSubmitText = this.getChangeProSubmitText('existingPro');
    this.changeProErrorMessage = '';
    this.changeProSubmitted = false;
    this.changeProCountryCode = '';
    this.setChangeProValidation('existingPro');
  }

  private setChangeProValidation(type: string) {
    if (type === 'existingPro') {
      this.changeProForm.controls.privatePro.setValidators([Validators.required]);
      this.changeProForm.controls.name.clearValidators();
      this.changeProForm.controls.name.updateValueAndValidity();
      this.changeProForm.controls.email.clearValidators();
      this.changeProForm.controls.email.updateValueAndValidity();
      this.changeProForm.controls.phone.clearValidators();
      this.changeProForm.controls.phone.updateValueAndValidity();
    } else {
      this.changeProForm.controls.name.setValidators([Validators.required]);
      this.changeProForm.controls.email.setValidators([Validators.required, validationUtils.validateEmail]);
      this.changeProForm.controls.phone.setValidators([Validators.minLength(10)]);
      this.changeProForm.controls.privatePro.clearValidators();
      this.changeProForm.controls.privatePro.updateValueAndValidity();
    }
  }

  private getChangeProSubmitText(type: string): string {
    if (type === 'newPro') {
      return this.changeProHeaderText === 'Change' ? 'Add & Change Pro' : 'Add & Assign Pro';
    }
    return this.changeProHeaderText === 'Change' ? 'Change Pro' : 'Assign Pro';
  }

  onChangeProTypeSelect(selection: any) {
    this.setChangeProValidation(selection.value);
    this.changeProSubmitText = this.getChangeProSubmitText(selection.value);
  }

  onChangeProCountryCodeChange(countryCode: string) {
    this.changeProCountryCode = countryCode.replace('+', '');
  }

  async confirmChangePro() {
    this.changeProErrorMessage = '';
    this.changeProSubmitted = true;
    if (!this.changeProForm.valid) {
      return;
    }
    try {
      if (this.changeProForm.value.existingOrNew === 'newPro') {
        const newPro = await this.addNewProForJob();
        if (!newPro) {return;}
        await this.assignProToJob(newPro);
      } else {
        const selectedItem = this.changeProPrivateProItems.find(
          item => item.value === this.changeProForm.value.privatePro
        );
        await this.assignProToJob({
          name: selectedItem?.viewValue,
          id: this.changeProForm.value.privatePro
        });
      }
    } catch (err) {
      this.changeProErrorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  private async addNewProForJob(): Promise<any> {
    const servicetypes = [1];
    const serviceTypeId = +this.booking?.service_type_details?.service_category?.id;
    if (serviceTypeId && !servicetypes.includes(serviceTypeId)) {
      servicetypes.push(serviceTypeId);
    }
    try {
      const params: any = {
        name: this.changeProForm.value.name,
        email: this.changeProForm.value.email,
        add_to_address_homekeeper_list: {
          address_id: this.addressId,
          service_type_ids: servicetypes
        }
      };
      if (this.changeProForm.value.phone) {
        params.phone = parsePhoneNumber(this.changeProCountryCode + this.changeProForm.value.phone);
      }
      const pro = await this.prosService.addNewPro(params);
      await this.onboardingProvider.markGoalAsCompleted('addPros');
      return pro;
    } catch (err) {
      this.changeProErrorMessage = err.error ? err.error.message : err.message;
      return null;
    }
  }

  private async assignProToJob(pro: { name: string; id: number }) {
    try {
      const proDetails = await this.prosService.getProDetail(pro.id);
      await this.bookingProvider.assignPrivatePro(this.bookingId, proDetails.team.id);
      this.showChangeProPopup = false;
      const successMessage = `${new TranslationPipe().transform('Successfully assigned ')}${proDetails.homekeeper.name}!`;
      this.util.showSuccess(successMessage);
      localStorage.setItem('madeUpdateToSchedule', 'true');
      const updatedBooking = await this.schedule.getBooking(this.bookingId);
      const updatedJobId = updatedBooking.booking.current_job.id;
      this.proChanged.emit({booking: updatedBooking, jobId: updatedJobId});
    } catch (err) {
      this.changeProErrorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  closeChangeProPopup() {
    this.showChangeProPopup = false;
    this.changeProErrorMessage = '';
  }
}
